/**
 * 账号批量导入与开通页面
 * 管理员可以批量导入账号，自动完成 OneAPI 账户创建、Token 初始化、VIP 开通
 */

import { useState, useEffect } from 'react';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Upload,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Eye,
  Loader2,
  Download,
} from 'lucide-react';
import { accountActivationApi, type AccountActivation, type AccountItem, type BatchStatus } from '@/services/accountActivationApi';

export function AccountActivationPage() {
  // 导入表单状态
  const [accountsText, setAccountsText] = useState('');
  const [platform, setPlatform] = useState('xiaozhi');
  const [productCode, setProductCode] = useState('');
  const [importing, setImporting] = useState(false);

  // 批次状态
  const [currentBatch, setCurrentBatch] = useState<BatchStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [lastBatchId, setLastBatchId] = useState<string | null>(null);

  // 历史记录
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyItems, setHistoryItems] = useState<AccountActivation[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyBatchId, setHistoryBatchId] = useState('');

  // 详情弹窗
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BatchStatus | null>(null);

  // 解析账号文本
  const parseAccounts = (text: string): AccountItem[] => {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => {
      const parts = line.split(/[\t,，]/);
      const account: AccountItem = {
        platform_account: parts[0]?.trim() || '',
        email: parts[1]?.trim() || '',
        token_quota: parts[2] ? parseInt(parts[2].trim(), 10) || 0 : 0,
        vip_duration: parts[3] ? parseInt(parts[3].trim(), 10) || 0 : 0,
        notes: parts[4]?.trim() || '',
      };
      return account;
    }).filter(item => item.platform_account && item.email);
  };

  // 导入账号
  const handleImport = async () => {
    const accounts = parseAccounts(accountsText);
    if (accounts.length === 0) {
      toast.error('请输入至少一个有效的账号（需包含账号和邮箱）');
      return;
    }
    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = accounts.filter(a => !emailRegex.test(a.email));
    if (invalidEmails.length > 0) {
      toast.error(`以下账号邮箱格式不正确：${invalidEmails.map(a => a.platform_account).join(', ')}`);
      return;
    }
    if (!productCode) {
      toast.error('请选择产品代码');
      return;
    }

    setImporting(true);
    try {
      const result = await accountActivationApi.importAccounts({
        accounts,
        product_code: productCode,
        platform,
      });
      setLastBatchId(result.batch_id);
      toast.success(`成功导入 ${result.stats.success} 条记录，正在后台处理`);
      setAccountsText('');
      // 开始轮询状态
      startPolling(result.batch_id);
    } catch (error: any) {
      toast.error('导入失败', { description: error.message });
    } finally {
      setImporting(false);
    }
  };

  // 轮询批次状态
  const startPolling = (batchId: string) => {
    setPolling(true);
    pollBatchStatus(batchId);
  };

  const pollBatchStatus = async (batchId: string) => {
    try {
      const status = await accountActivationApi.getBatchStatus(batchId);
      setCurrentBatch(status);
      // 如果全部处理完成，停止轮询
      const pending = status.stats.pending;
      if (pending === 0) {
        setPolling(false);
      } else {
        // 继续轮询
        setTimeout(() => pollBatchStatus(batchId), 2000);
      }
    } catch (error) {
      console.error('轮询批次状态失败', error);
      setPolling(false);
    }
  };

  // 查看历史
  const loadHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const result = await accountActivationApi.getActivationList({
        page,
        page_size: historyPageSize,
        batch_id: historyBatchId || undefined,
      });
      setHistoryItems(result.items);
      setHistoryTotal(result.total);
      setHistoryPage(page);
    } catch (error: any) {
      toast.error('加载历史记录失败', { description: error.message });
    } finally {
      setHistoryLoading(false);
    }
  };

  // 下载模板
  const handleDownloadTemplate = async () => {
    try {
      await accountActivationApi.downloadTemplate();
      toast.success('模板下载成功');
    } catch (error: any) {
      toast.error('下载模板失败', { description: error.message });
    }
  };

  // 查看批次详情
  const handleViewBatch = async (batchId: string) => {
    try {
      const status = await accountActivationApi.getBatchStatus(batchId);
      setSelectedBatch(status);
      setDetailDialogOpen(true);
    } catch (error: any) {
      toast.error('加载批次详情失败', { description: error.message });
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  // 轮询控制
  useEffect(() => {
    if (!polling || !lastBatchId) return;
    pollBatchStatus(lastBatchId);
  }, [polling, lastBatchId]);

  // 获取状态徽章
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />成功</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />失败</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />处理中</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // 格式化时间
  const formatTime = (time?: string) => {
    if (!time) return '-';
    return new Date(time).toLocaleString('zh-CN');
  };

  // 产品代码选项
  const productCodeOptions = [
    { value: 'ring_299', label: '智能戒指(基础版) ¥299' },
    { value: 'gateway_399', label: '智能网关 ¥399' },
    { value: 'ring_599', label: '智能戒指(高级版) ¥599' },
    { value: 'combo_any', label: '戒指+网关组合' },
    { value: 'vip_1m', label: 'VIP一个月' },
    { value: 'vip_1y', label: 'VIP一年' },
    { value: 'vvip_1y', label: 'VVIP一年' },
  ];

  return (
    <LayoutWithSidebar title="账号批量导入">
      <div className="container mx-auto py-4 px-3 sm:py-8 sm:px-4 max-w-7xl">
        {/* 标题区域 */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">账号批量导入与开通</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            批量导入账号，自动完成 OneAPI 账户创建、Token 初始化、VIP 开通
          </p>
        </div>

        <Tabs defaultValue="import" className="space-y-4">
          <TabsList>
            <TabsTrigger value="import">
              <Upload className="w-4 h-4 mr-2" />
              导入账号
            </TabsTrigger>
            <TabsTrigger value="history">
              <Users className="w-4 h-4 mr-2" />
              历史记录
            </TabsTrigger>
          </TabsList>

          {/* 导入标签页 */}
          <TabsContent value="import" className="space-y-4">
            {/* 导入表单 */}
            <Card>
              <CardHeader>
                <CardTitle>批量导入</CardTitle>
                <CardDescription>
                  每行一个账号，格式：账号,邮箱,Token额度,备注
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-muted-foreground">
                    <span className="text-red-500">*</span> 表示必填字段。vip_duration 为 VIP 时长（天），可选填，默认30天
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    下载模板
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="platform">平台</Label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger id="platform">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="xiaozhi">小智AI</SelectItem>
                        <SelectItem value="alita">Alita Planet</SelectItem>
                        <SelectItem value="other">其他</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="productCode">产品代码 <span className="text-red-500">*</span></Label>
                    <Select value={productCode} onValueChange={setProductCode}>
                      <SelectTrigger id="productCode">
                        <SelectValue placeholder="选择产品代码" />
                      </SelectTrigger>
                      <SelectContent>
                        {productCodeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="accounts">账号列表 <span className="text-red-500">*</span></Label>
                  <Textarea
                    id="accounts"
                    placeholder={`输入账号列表，每行一个：\nzhangsan,zhangsan@example.com,1000,30,VIP用户-30天\nlisi,lisi@example.com,500,90\nwangwu,wangwu@example.com,2000,365`}
                    value={accountsText}
                    onChange={(e) => setAccountsText(e.target.value)}
                    rows={10}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    格式：账号名称,邮箱,Token额度,VIP时长(天),备注，使用逗号或Tab分隔
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setAccountsText('')}
                    disabled={!accountsText || importing}
                  >
                    清空
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={importing || !accountsText || !productCode}
                  >
                    {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {importing ? '导入中...' : '开始导入'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 当前批次状态 */}
            {currentBatch && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>当前批次状态</CardTitle>
                      <CardDescription>批次ID: {currentBatch.batch_id}</CardDescription>
                    </div>
                    {polling && <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <div className="text-2xl font-bold">{currentBatch.stats.total}</div>
                      <div className="text-sm text-muted-foreground">总计</div>
                    </div>
                    <div className="text-center p-4 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{currentBatch.stats.success}</div>
                      <div className="text-sm text-muted-foreground">成功</div>
                    </div>
                    <div className="text-center p-4 bg-red-100 dark:bg-red-900/30 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{currentBatch.stats.failed}</div>
                      <div className="text-sm text-muted-foreground">失败</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{currentBatch.stats.pending}</div>
                      <div className="text-sm text-muted-foreground">处理中</div>
                    </div>
                  </div>

                  {currentBatch.stats.pending === 0 && (
                    <Button onClick={() => loadHistory()}>刷新历史记录</Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* 历史记录标签页 */}
          <TabsContent value="history" className="space-y-4">
            {/* 筛选 */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="按批次ID筛选..."
                      value={historyBatchId}
                      onChange={(e) => setHistoryBatchId(e.target.value)}
                    />
                  </div>
                  <Button variant="outline" onClick={() => { setHistoryBatchId(''); loadHistory(1); }}>
                    重置
                  </Button>
                  <Button onClick={() => loadHistory(1)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    刷新
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 历史列表 */}
            <Card>
              <CardHeader>
                <CardTitle>开通记录</CardTitle>
                <CardDescription>共 {historyTotal} 条记录</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>批次ID</TableHead>
                        <TableHead>平台账号</TableHead>
                        <TableHead>邮箱</TableHead>
                        <TableHead>平台</TableHead>
                        <TableHead>VIP等级</TableHead>
                        <TableHead>VIP时长</TableHead>
                        <TableHead>Token额度</TableHead>
                        <TableHead>状态</TableHead>
                        <TableHead>开通时间</TableHead>
                        <TableHead className="text-right">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyLoading ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                          </TableCell>
                        </TableRow>
                      ) : historyItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                            暂无记录
                          </TableCell>
                        </TableRow>
                      ) : (
                        historyItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-xs">{item.batch_id.slice(0, 8)}...</TableCell>
                            <TableCell className="font-medium">{item.platform_account}</TableCell>
                            <TableCell className="text-sm">{item.email || '-'}</TableCell>
                            <TableCell>{item.platform}</TableCell>
                            <TableCell>
                              {item.vip_level > 0 ? `VIP ${item.vip_level}` : '-'}
                            </TableCell>
                            <TableCell>{item.vip_duration > 0 ? `${item.vip_duration} 天` : '-'}</TableCell>
                            <TableCell>{item.token_quota > 0 ? item.token_quota : '-'}</TableCell>
                            <TableCell>{getStatusBadge(item.status)}</TableCell>
                            <TableCell className="text-sm">{formatTime(item.activated_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewBatch(item.batch_id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                查看
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* 分页 */}
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    第 {historyPage} / {Math.ceil(historyTotal / historyPageSize) || 1} 页
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadHistory(historyPage - 1)}
                      disabled={historyPage <= 1}
                    >
                      上一页
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadHistory(historyPage + 1)}
                      disabled={historyPage >= Math.ceil(historyTotal / historyPageSize)}
                    >
                      下一页
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* 批次详情弹窗 */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>批次详情</DialogTitle>
              <DialogDescription>批次ID: {selectedBatch?.batch_id}</DialogDescription>
            </DialogHeader>
            {selectedBatch && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <div className="text-2xl font-bold">{selectedBatch.stats.total}</div>
                    <div className="text-sm text-muted-foreground">总计</div>
                  </div>
                  <div className="text-center p-4 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{selectedBatch.stats.success}</div>
                    <div className="text-sm text-muted-foreground">成功</div>
                  </div>
                  <div className="text-center p-4 bg-red-100 dark:bg-red-900/30 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{selectedBatch.stats.failed}</div>
                    <div className="text-sm text-muted-foreground">失败</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{selectedBatch.stats.pending}</div>
                    <div className="text-sm text-muted-foreground">处理中</div>
                  </div>
                </div>

                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>平台账号</TableHead>
                        <TableHead>邮箱</TableHead>
                        <TableHead>VIP等级</TableHead>
                        <TableHead>VIP时长</TableHead>
                        <TableHead>Token额度</TableHead>
                        <TableHead>状态</TableHead>
                        <TableHead>错误信息</TableHead>
                        <TableHead>用户ID</TableHead>
                        <TableHead>OneAPI ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedBatch.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.platform_account}</TableCell>
                          <TableCell className="text-sm">{item.email || '-'}</TableCell>
                          <TableCell>{item.vip_level > 0 ? `VIP ${item.vip_level}` : '-'}</TableCell>
                          <TableCell>{item.vip_duration > 0 ? `${item.vip_duration} 天` : '-'}</TableCell>
                          <TableCell>{item.token_quota > 0 ? item.token_quota : '-'}</TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                          <TableCell className="text-sm text-red-500 max-w-[200px] truncate">
                            {item.error_msg || '-'}
                          </TableCell>
                          <TableCell className="font-mono">{item.user_id || '-'}</TableCell>
                          <TableCell className="font-mono">{item.oneapi_user_id || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>关闭</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LayoutWithSidebar>
  );
}
